import { useEffect, useState, type ReactNode } from 'react';
import type { NegocioDelPartner, ResumenDelPartner } from '../api/ops/partners';
import { ErrorApi } from '../api/problema';
import { Chip } from '../componentes/Chip';
import { Icono } from '../componentes/Icono';
import type { NombreIcono } from '../componentes/iconos';
import { Menu } from '../componentes/Menu';
import { Paginacion } from '../componentes/Paginacion';
import { conY } from '../logica/acceso';
import { dinero } from '../logica/dinero';
import { iniciales } from '../logica/navegacion';
import { PRIMERA_PAGINA, avanzar, cursorDe, hayAnterior, haySiguiente, retroceder } from '../logica/paginacion';
import { useCabecera, type AccionCabecera } from '../marco/cabecera';
import {
    SUBTITULOS,
    ayudaDe,
    claveMes,
    comisionDelNegocio,
    comisionesDelMes,
    conImpago,
    lineaNegocio,
    liquidacionEnCurso,
    mesCorto,
    mesDe,
    mesDePeriodo,
    mesSiguiente,
    nombreMes,
    reglasAtribucion,
    textoMes,
    type Mes,
} from './logica';
import { useCarga, type DatosPortal } from './carga';
import { descargarQr, useCopiar, useHecho } from './copiar';
import { BarrasH, BarrasMes, BotonCopiar, Fila } from './piezas';

const AVISO_ENLACE = 'Copiado tu enlace de alta. Quien se dé de alta con él queda atribuido a ti.';
const mesDeHoy = (): Mes => mesDe(new Date().toISOString());

/**
 * «Copiar mi enlace» en la banda negra (Inicio y Negocios), que se queda en «Enlace copiado» un momento. Sin enlace (la
 * solicitud en revisión, G1108), no hay acción.
 */
function useAccionCopiarEnlace(d: DatosPortal): AccionCabecera[] {
    const copiar = useCopiar();
    const [hecho, marcar] = useHecho();
    const enlace = d.enlace;
    if (enlace === null || d.movil) {
        return [];
    }
    return [{
        clave: hecho ? 'copiado' : 'copiar',
        texto: hecho ? 'Enlace copiado' : 'Copiar mi enlace',
        icono: hecho ? 'check' : 'copy',
        secundaria: true,
        onClick: () => {
            void copiar(enlace, AVISO_ENLACE).then((ok) => {
                if (ok) {
                    marcar();
                }
            });
        },
    }];
}

/** «Viernes 16 de octubre · Agencia Nocturna». */
function hoyCon(nombre: string): string {
    const hoy = new Date();
    const dia = hoy.toLocaleDateString('es-ES', { weekday: 'long', timeZone: 'Europe/Madrid' });
    const fecha = hoy.toLocaleDateString('es-ES', { day: 'numeric', month: 'long', timeZone: 'Europe/Madrid' });
    return `${dia.charAt(0).toUpperCase()}${dia.slice(1)} ${fecha} · ${nombre}`;
}

// ---- Inicio: título con la buena noticia → accesos → zigzag «Cómo te va» → para revisar ----

const ACCESOS: readonly { clave: string; etiqueta: string; icono: NombreIcono; area: 'traer' | 'dinero'; principal?: boolean }[] = [
    { clave: 'copiar', etiqueta: 'Copiar mi enlace', icono: 'copy', area: 'traer', principal: true },
    { clave: 'codigo', etiqueta: 'Copiar mi código', icono: 'ticket', area: 'traer' },
    { clave: 'negocios', etiqueta: 'Mis negocios', icono: 'building-2', area: 'traer' },
    { clave: 'comisiones', etiqueta: 'Mis comisiones', icono: 'percent', area: 'dinero' },
    { clave: 'liquidaciones', etiqueta: 'Liquidaciones', icono: 'wallet', area: 'dinero' },
    { clave: 'datos', etiqueta: 'Datos fiscales', icono: 'file-text', area: 'dinero' },
];

/**
 * Inicio. Las sumas («este mes te llevas», lo ya pagado en el año con `year_paid`, G1106, y lo que pagan tus negocios al
 * mes) son de `getPartnerSummary` (G1094); «En N liquidaciones», sin «a tiempo», que la API no da (G1107, C.59). En
 * revisión no hay enlace ni código (G1108): sin sus accesos ni sus botones; mientras llega, el título usa el total de la liquidación en curso. Las filas del zigzag son
 * las del prototipo (RV Zigzag: frase con el dato en rojo y su gráfica, alternas).
 */
export function Inicio({ d }: { d: DatosPortal }) {
    const { perfil, pct } = d;
    const copiar = useCopiar();
    const acciones = useAccionCopiarEnlace(d);
    useCabecera({ ayuda: ayudaDe('inicio', pct), acciones, subtitulo: hoyCon(perfil.legal_name) });
    const negocios = useCarga(() => d.ops.listPartnerBusinesses({ limit: 100 }), d.onSesionCaducada);
    const resumen = useCarga<ResumenDelPartner>(() => d.ops.getPartnerSummary(), d.onSesionCaducada).datos;

    const mes = mesDeHoy();
    const sig = nombreMes(mesSiguiente(mes));
    const liquidaciones = d.liquidaciones.datos?.data ?? [];
    const enCurso = liquidacionEnCurso(liquidaciones);
    const delMes = comisionesDelMes(d.comisiones.datos?.data ?? [], mes).filter((c) => c.status !== 'reversed');
    const listaNegocios = negocios.datos?.data ?? [];
    const conImpagos = listaNegocios.filter(conImpago);
    const [primera, ...resto] = delMes;
    const moneda = primera?.amount.currency ?? 'EUR';
    const comisionDelMes = resumen?.month_commission ?? enCurso?.total ?? { amount: 0, currency: moneda };
    // Los últimos seis meses (hasta el de hoy), con el total de la liquidación de cada uno; sin liquidación, 0.
    const seisMeses = Array.from({ length: 6 }, (_, i) => {
        const m = { y: mes.y, m: mes.m - 5 + i };
        return m.m < 0 ? { y: m.y - 1, m: m.m + 12 } : m;
    }).map((m) => ({ m, l: liquidaciones.find((l) => claveMes(mesDePeriodo(l.period)) === claveMes(m)) }));
    const pagadasDelAno = liquidaciones.filter((l) => l.status === 'paid' && mesDePeriodo(l.period).y === mes.y).length;
    // En revisión no hay enlace ni código (G1108): sin sus accesos.
    const { enlace } = d;
    const codigo = perfil.referral_code;
    const accesos = ACCESOS.filter((a) => (a.clave !== 'copiar' || enlace !== null) && (a.clave !== 'codigo' || codigo !== null));

    const ir = (clave: string) => {
        switch (clave) {
            case 'copiar':
                if (enlace !== null) {
                    void copiar(enlace, AVISO_ENLACE);
                }
                return;
            case 'codigo':
                if (codigo !== null) {
                    void copiar(codigo, `Copiado tu código ${codigo}.`);
                }
                return;
            case 'negocios':
            case 'comisiones':
            case 'liquidaciones':
            case 'datos':
                d.ir(clave);
        }
    };

    return (
        <div className="rv-pt-inicio">
            <div className="rv-pt-inicio__titulo">
                <p>
                    {`${perfil.legal_name}, este mes te llevas `}
                    <em>{dinero(comisionDelMes)}</em>.
                </p>
                {delMes.length > 0 && <span>{`Por las cuotas de ${nombreMes(mes)} de ${conY(delMes.map((c) => c.business_name))}. Te lo pagamos antes del 15 de ${sig}.`}</span>}
            </div>

            <section aria-labelledby="rv-pt-accesos" className="rv-accesos">
                <div className="rv-accesos__cab">
                    <h2 id="rv-pt-accesos">Tus accesos</h2>
                </div>
                {(['traer', 'dinero'] as const).map((area) => (
                    <div key={area} role="group" aria-label={area === 'traer' ? 'Traer negocios' : 'Tu dinero'} className="rv-pt-accesos__area">
                        <span className="rv-pt-ceja">{area === 'traer' ? 'Traer negocios' : 'Tu dinero'}</span>
                        <div className="rv-accesos__rejilla">
                            {accesos.filter((a) => a.area === area).map((a) => (
                                <button key={a.clave} type="button" className="rv-accesos__baldosa" onClick={() => { ir(a.clave); }}>
                                    <span className="rv-accesos__icono" style={{ background: a.principal === true ? 'var(--rv-red)' : 'var(--rv-ink)' }}>
                                        <Icono nombre={a.icono} />
                                    </span>
                                    <span className="rv-accesos__texto">{a.etiqueta}</span>
                                </button>
                            ))}
                        </div>
                    </div>
                ))}
            </section>

            <div className="rv-pt-zigzags" role="group" aria-label="Cómo te va">
                {primera !== undefined && (
                    <FilaZigzag
                        frase={
                            <>
                                {`${primera.business_name} te deja `}
                                <em>{dinero(primera.amount)}</em>
                                {` en ${nombreMes(mes)}.`}
                            </>
                        }
                        linea={`${resto.map((c) => `${c.business_name}, ${dinero(c.amount)}`).join(' y ')}${resto.length > 0 ? '. ' : ''}Te lo pagamos antes del 15 de ${sig}.`}
                        verMas="Ver comisiones"
                        onVerMas={() => { d.ir('comisiones'); }}
                        titulo={`Tu comisión de ${nombreMes(mes)}`}
                        cifra={dinero(comisionDelMes)}
                    >
                        <BarrasH
                            etiqueta={`Tu comisión de ${nombreMes(mes)} por negocio`}
                            filas={delMes.map((c) => ({ clave: c.id, nombre: c.business_name, valor: c.amount.amount, cifra: dinero(c.amount), detalle: `${c.business_name} · ${pct} de una cuota de ${dinero(c.base)}` }))}
                        />
                    </FilaZigzag>
                )}
                {resumen !== null && (
                    <FilaZigzag
                        inversa
                        frase={
                            <>
                                {'Llevas '}
                                <em>{dinero(resumen.year_paid)}</em>
                                {` cobrados en ${String(mes.y)}.`}
                            </>
                        }
                        linea={pagadasDelAno > 0 ? `En ${String(pagadasDelAno)} ${pagadasDelAno === 1 ? 'liquidación' : 'liquidaciones'}.` : ''}
                        verMas="Ver liquidaciones"
                        onVerMas={() => { d.ir('liquidaciones'); }}
                        titulo="Tu comisión por mes"
                    >
                        <BarrasMes
                            barras={seisMeses.map(({ m, l }) => ({ clave: claveMes(m), corta: mesCorto(m), larga: textoMes(m), valor: l?.total.amount ?? 0, cifra: dinero(l?.total ?? { amount: 0, currency: moneda }) }))}
                            elegida={claveMes(mes)}
                            onElegir={(clave) => { d.ir('comisiones', `&mes=${encodeURIComponent(clave)}`); }}
                        />
                    </FilaZigzag>
                )}
                {resumen !== null && (
                    <FilaZigzag
                        frase={
                            <>
                                {'Traes '}
                                <em>{`${String(resumen.active_businesses)} ${resumen.active_businesses === 1 ? 'negocio' : 'negocios'}`}</em>
                                {` que ${resumen.active_businesses === 1 ? 'paga' : 'pagan'} ${dinero(resumen.monthly_fees_total)} al mes.`}
                            </>
                        }
                        linea={`Tu parte es el ${pct} de sus cuotas sin IVA, mientras sigan dados de alta.`}
                        copiar={enlace === null ? undefined : <BotonCopiar texto={enlace} etiqueta="Copiar mi enlace" hecho="Enlace copiado" aviso={AVISO_ENLACE} movil={d.movil} />}
                        verMas="Ver mis negocios"
                        onVerMas={() => { d.ir('negocios'); }}
                        titulo="Cuota de cada negocio"
                        cifra={`${dinero(resumen.monthly_fees_total)} al mes`}
                    >
                        <BarrasH
                            etiqueta="Cuota de cada negocio"
                            filas={listaNegocios.map((n) => ({ clave: n.business_id, nombre: n.business_name, valor: n.monthly_fee.amount, cifra: dinero(n.monthly_fee), detalle: conImpago(n) ? `${n.business_name} · cuota sin pagar` : n.business_name }))}
                        />
                    </FilaZigzag>
                )}
            </div>

            {conImpagos.length > 0 && (
                <section aria-labelledby="rv-pt-revisar" className="rv-pt-revisar">
                    <span id="rv-pt-revisar" className="rv-pt-ceja">Para revisar</span>
                    <div className="rv-pt-lista">
                        {conImpagos.map((n) => (
                            <button key={n.business_id} type="button" className="rv-pt-aviso" onClick={() => { d.ir('negocios'); }}>
                                <Icono nombre="circle-alert" style={{ color: 'var(--rv-red)' }} />
                                <span>
                                    <b>{`${n.business_name} no ha pagado la cuota de ${nombreMes(mes)}`}</b>
                                    <span> · Mientras no la pague, este mes no suma a tu comisión.</span>
                                </span>
                                <span className="rv-pt-aviso__ver">
                                    Ver
                                    <Icono nombre="chevron-right" />
                                </span>
                            </button>
                        ))}
                    </div>
                </section>
            )}
        </div>
    );
}

/** Una fila del zigzag de Inicio: frase grande con el dato en rojo y su línea; a la otra mitad, la tarjeta con la gráfica. */
function FilaZigzag({ frase, linea, verMas, onVerMas, titulo, cifra, copiar, inversa = false, children }: { frase: ReactNode; linea: string; verMas: string; onVerMas: () => void; titulo: string; cifra?: string; copiar?: ReactNode; inversa?: boolean; children: ReactNode }) {
    return (
        <section className={`rv-pt-zigzag${inversa ? ' rv-pt-zigzag--inversa' : ''}`}>
            <div className="rv-pt-zigzag__texto">
                <p>{frase}</p>
                {linea !== '' && <span>{linea}</span>}
                {copiar}
                <button type="button" className="rv-pt-enlace" onClick={onVerMas}>
                    <span>{verMas}</span>
                    <Icono nombre="chevron-right" />
                </button>
            </div>
            <div className="rv-pt-tarjeta">
                <strong className="rv-pt-tarjeta__titulo">{titulo}</strong>
                {cifra !== undefined && <span className="rv-pt-tarjeta__cifra rv-num">{cifra}</span>}
                {children}
            </div>
        </section>
    );
}

// ---- Negocios: filas limpias (siglas, nombre y una línea; tu comisión grande a la derecha) ----

export function Negocios({ d }: { d: DatosPortal }) {
    const acciones = useAccionCopiarEnlace(d);
    useCabecera({ ayuda: ayudaDe('negocios', d.pct), acciones, subtitulo: SUBTITULOS.negocios });
    const [recorrido, setRecorrido] = useState(PRIMERA_PAGINA);
    const pagina = usePagina(d, cursorDe(recorrido));
    const mes = mesDeHoy();
    const comisiones = d.comisiones.datos?.data ?? [];
    const lista = pagina.datos?.data ?? null;

    return (
        <div className="rv-pt-seccion">
            {d.movil && d.enlace !== null && (
                <div className="rv-acciones-m">
                    <BotonCopiar texto={d.enlace} etiqueta="Copiar mi enlace" hecho="Enlace copiado" aviso={AVISO_ENLACE} movil />
                </div>
            )}
            {lista === null ? (
                pagina.error !== null ? <p className="rv-pt-nota">No hemos podido cargar tus negocios. Prueba de nuevo.</p> : <div className="rv-pt-esqueleto" aria-busy="true" />
            ) : lista.length === 0 ? (
                <div className="rv-pt-lista">
                    <p className="rv-pt-vacia">Todavía no hay negocios con tu enlace o tu código.</p>
                </div>
            ) : (
                <div className="rv-pt-lista">
                    {lista.map((n) => (
                        <FilaNegocio key={n.business_id} n={n} d={d} mes={mes} comision={comisionDelNegocio(comisiones, n.business_id, mes)?.amount ?? null} />
                    ))}
                </div>
            )}
            {(hayAnterior(recorrido) || haySiguiente(pagina.datos?.next_cursor)) && (
                <Paginacion
                    hayAnterior={hayAnterior(recorrido)}
                    haySiguiente={haySiguiente(pagina.datos?.next_cursor)}
                    onAnterior={() => { setRecorrido(retroceder(recorrido)); }}
                    onSiguiente={() => { setRecorrido(avanzar(recorrido, pagina.datos?.next_cursor)); }}
                    movil={d.movil}
                />
            )}
            <p className="rv-pt-pie">{`Ves el nombre, el estado y la cuota de cada negocio. Su panel y sus clientes son solo suyos. Las cuotas son sin IVA.`}</p>
        </div>
    );
}

/** Una página de negocios (cada cursor, su petición); un 401 sin renovación acaba la sesión. */
function usePagina(d: DatosPortal, cursor: string | null) {
    const [estado, setEstado] = useState<{ cursor: string | null; datos: { data: NegocioDelPartner[]; next_cursor: string | null } | null; error: unknown }>({ cursor: null, datos: null, error: null });
    const { ops, onSesionCaducada } = d;
    useEffect(() => {
        let vivo = true;
        ops.listPartnerBusinesses({ cursor, limit: 25 })
            .then((datos) => {
                if (vivo) {
                    setEstado({ cursor, datos, error: null });
                }
            })
            .catch((error: unknown) => {
                if (!vivo) {
                    return;
                }
                if (error instanceof ErrorApi && error.estado === 401) {
                    onSesionCaducada();
                    return;
                }
                setEstado({ cursor, datos: null, error });
            });
        return () => {
            vivo = false;
        };
    }, [ops, cursor, onSesionCaducada]);
    return estado.cursor === cursor ? estado : { cursor, datos: null, error: null };
}

/** «Ver sus comisiones» abre Comisiones con `?negocio=` (su `business_id`), que pide las comisiones con ese filtro (G1098, G1104). */
function FilaNegocio({ n, d, mes, comision }: { n: NegocioDelPartner; d: DatosPortal; mes: Mes; comision: { amount: number; currency: string } | null }) {
    const impago = conImpago(n);
    const sinCobrar = impago || comision === null;
    return (
        <Fila
            siglas={iniciales(n.business_name)}
            titulo={n.business_name}
            chip={impago ? <Chip estado="problem">Impago</Chip> : undefined}
            linea={lineaNegocio(n, d.movil)}
            cifra={dinero(sinCobrar ? { amount: 0, currency: n.monthly_fee.currency } : comision)}
            cifraApagada={sinCobrar}
            bajoCifra={sinCobrar ? (d.movil ? 'sin cobrar' : `${nombreMes(mes)} sin cobrar`) : d.movil ? `tu ${d.pct}` : `tu ${d.pct} en ${nombreMes(mes)}`}
            accion={
                <Menu
                    etiqueta={`Más opciones de ${n.business_name}`}
                    movil={d.movil}
                                        opciones={[{ clave: 'comisiones', texto: 'Ver sus comisiones', icono: 'percent', onClick: () => { d.ir('comisiones', `&negocio=${encodeURIComponent(n.business_id)}`); } }]}
                />
            }
        />
    );
}

// ---- Tu enlace: nunca la URL pintada entera; RV Copiar a la vista y lo demás en «⋯» ----

export function TuEnlace({ d }: { d: DatosPortal }) {
    const { perfil, pct } = d;
    const copiar = useCopiar();
    useCabecera({ ayuda: ayudaDe('enlace', pct), acciones: [], subtitulo: SUBTITULOS.enlace });
    const [abierta, setAbierta] = useState(false);
    // En revisión no hay enlace ni código (G1108): solo «Cómo funciona la atribución»; arriba, «Solicitud enviada».
    const codigo = perfil.referral_code;
    const enlace = d.enlace;
    return (
        <div className="rv-pt-seccion">
            {enlace !== null && codigo !== null && (
                <div className="rv-pt-lista">
                    <Fila
                        icono="link"
                        titulo="Tu enlace de alta"
                        linea="Quien se dé de alta con él queda atribuido a ti."
                        accion={
                            <span className="rv-pt-fila__acciones">
                                <BotonCopiar texto={enlace} etiqueta="Copiar mi enlace" hecho="Enlace copiado" aviso={AVISO_ENLACE} />
                                <Menu
                                    etiqueta="Más opciones de tu enlace"
                                    movil={d.movil}
                                    opciones={[
                                        { clave: 'codigo', texto: `Copiar el código ${codigo}`, icono: 'ticket', onClick: () => { void copiar(codigo, `Copiado tu código ${codigo}.`); } },
                                        { clave: 'qr', texto: 'Descargar el código QR', icono: 'qr-code', detalle: 'Para imprimirlo en tus presentaciones.', onClick: () => { descargarQr(enlace, `reservaria-${codigo.toLowerCase()}.svg`); } },
                                    ]}
                                />
                            </span>
                        }
                    />
                    <div className="rv-pt-fila">
                        <Icono nombre="ticket" className="rv-pt-fila__icono" />
                        <span className="rv-pt-fila__textos">
                            <span className="rv-pt-fila__titulo">
                                <span>Tu código</span>
                                <span className="rv-pt-codigo rv-num">{codigo}</span>
                            </span>
                            <span className="rv-pt-fila__linea">Para quien se da de alta sin el enlace: lo escribe en «¿Tienes un código de partner?».</span>
                        </span>
                        <span className="rv-pt-fila__acciones">
                            <BotonCopiar texto={codigo} etiqueta="Copiar código" hecho="Código copiado" aviso={`Copiado tu código ${codigo}.`} />
                        </span>
                    </div>
                </div>
            )}
            <div className="rv-pt-lista">
                <button type="button" className="rv-pt-desplegable" aria-expanded={abierta} onClick={() => { setAbierta(!abierta); }}>
                    <span className="rv-pt-desplegable__baldosa" aria-hidden="true">
                        <Icono nombre="handshake" />
                    </span>
                    <span className="rv-pt-fila__textos">
                        <span className="rv-pt-fila__titulo">
                            <span>Cómo funciona la atribución</span>
                        </span>
                        <span className="rv-pt-fila__linea">Un negocio, un partner, para siempre. Tu código no cambia.</span>
                    </span>
                    <Icono nombre="chevron-down" style={{ transform: abierta ? 'rotate(180deg)' : 'none' }} />
                </button>
                {abierta && (
                    <div className="rv-pt-reglas">
                        {reglasAtribucion(pct).map(([icono, texto]) => (
                            <div key={texto}>
                                <Icono nombre={icono} />
                                <span>{texto}</span>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}
